import React from "react";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import "./ActiveRateAgreement.scss";

import rateAgreementService from "../../services/RateAgreementService";
import utilService from "../../services/UtilService";
import { NextPayment, RateAgreementInfo } from "../../types/RateAgreementInfo";
import { useTranslation } from "react-i18next";
import { Suspend } from "../suspend/Suspend";

import { PaymentType } from "../../types/PaymentType";
import { mapPaymentMethod } from "../../utils/paymentMethods";
import FormatNumber from "../../components/FormatNumber";
import FormatDate from "../../components/FormatDate";
import { useCurrency, useRateAgreementTenantConfiguration } from "../../contexts/TenantContext";

export const ActiveRateAgreement = () => {
    const { t } = useTranslation();
    const configuration = useRateAgreementTenantConfiguration();
    const [isPageLoaded, setIsPageLoaded] = React.useState<boolean>(false);
    const [debtNumber, setDebtNumber] = React.useState<string>("");
    const [activeRateAgreement, setActiveRateAgreement] = React.useState<RateAgreementInfo>({} as RateAgreementInfo);
    const [nextPayment, setNextPayment] = React.useState<NextPayment | undefined>({});
    const currency = useCurrency();

    const isNumberOfOutstandingPaymentsVisible = activeRateAgreement.numberOfOutstandingPayments !== undefined && activeRateAgreement.numberOfOutstandingPayments > 0;

    React.useEffect(() => {
        initData().then(() => setIsPageLoaded(true));
    }, []);

    const initData = async () => {
        try {
            const debtNumber = await utilService.getDebtNo();
            const rateAgreement = await rateAgreementService.getActiveRateAgreement(debtNumber);
            if (rateAgreement === undefined) {
                return;
            }

            setActiveRateAgreement(rateAgreement);
            setDebtNumber(debtNumber);
            setNextPayment(findNextPayment(rateAgreement));
        } catch (e) {
            console.log(e);
        }
    };

    const findNextPayment = (rateAgreement: RateAgreementInfo): NextPayment | undefined => {
        if (rateAgreement.nextRatePayments) {
            return rateAgreement.nextRatePayments.find((value) => !value.suspended);
        }
        return undefined;
    };

    const suspensionPossible = (): boolean => {
        if (activeRateAgreement.nextRatePayments && activeRateAgreement.nextRatePayments.length >= 2) {
            return !!activeRateAgreement.nextRatePayments[0].suspensionPossible;
        }

        return false;
    };

    return (
        <div className={"active-rate-agreement-container"}>
            {isPageLoaded && activeRateAgreement.rateAgreementId && (
                <Panel>
                    <div className={"title"}>
                        <b>{t("RATE_AGREEMENT_INFO_SNIPPET.TITLE")}</b>
                    </div>

                    {activeRateAgreement.paymentMethod !== PaymentType.ABO_BANKTRANSFER && (
                        <div className={"row-wrapper"}>
                            <div>{t("RATE_AGREEMENT_INFO_SNIPPET.PAYMENT_TYPE")}</div>
                            <b>
                                <div data-test="paymentType">{t(mapPaymentMethod(activeRateAgreement.paymentMethod))}</div>
                            </b>
                        </div>
                    )}

                    <div className={"row-wrapper"}>
                        <div>{t("RATE_AGREEMENT_INFO_SNIPPET.RATE_AMOUNT")}</div>
                        <b>
                            <FormatNumber number={activeRateAgreement.rateAmount} currency={currency} dataTest={"rateAmount"} />
                        </b>
                    </div>

                    {nextPayment && (
                        <>
                            <div className={"row-wrapper"}>
                                <div>{t("RATE_AGREEMENT_INFO_SNIPPET.NEXT_PAYMENT_DATE")}</div>
                                <div data-test="nextPaymentDate">
                                    <b>
                                        <FormatDate dateString={nextPayment.dueDate as string} />
                                    </b>
                                </div>
                            </div>
                            {configuration?.numberOfOutstandingRatesEnabled && isNumberOfOutstandingPaymentsVisible && (
                                <div className={"row-wrapper"}>
                                    <div>{t("RATE_AGREEMENT_INFO_SNIPPET.NUMBER_OUTSTANDING_PAYMENTS")}</div>
                                    <div data-test="numberOutstandingPayments">
                                        <b>{activeRateAgreement.numberOfOutstandingPayments}</b>
                                    </div>
                                </div>
                            )}

                            {suspensionPossible() && (
                                <Suspend
                                    activeRateAgreement={activeRateAgreement}
                                    debtNumber={debtNumber}
                                    onSuspendResult={(nextPayment: NextPayment) => setNextPayment(nextPayment)}
                                />
                            )}
                        </>
                    )}
                </Panel>
            )}
        </div>
    );
};
